// src/lib/csv-bascule.js — CHAMBRES SORTIES DE BEDS24 : LES RELEVÉS CSV COMPTENT
// À PARTIR D'UNE DATE DE BASCULE (décisions de Dimitri du 03/10/2026, option A).
//
// ⚠️ DOUBLON CONTRÔLÉ : lib/csv-bascule.js est une COPIE À L'IDENTIQUE de ce
// fichier (seul l'en-tête diffère). Syntaxe CommonJS des deux côtés, comme
// resas-v2-source.js. Garde : src/lib/doublons-lib-conformite.test.js.
//
// ══ LE CONSTAT ══════════════════════════════════════════════════════════════
// Les chambres lc1, lc2, lc3 de Maison Zen (sous-location, source de
// facturation 'v2') ne sont plus dans Beds24 depuis le 02/10/2026. Leurs
// réservations arrivent désormais par les relevés CSV Airbnb / Booking, écrits
// dans le blob `fbz8-data`. Or la projection remplace, pour une chambre 'v2',
// les réservations du blob par la table `reservations` : sans ce module, les
// lignes CSV étaient masquées.
//
// ══ LA RÈGLE (option A) ═════════════════════════════════════════════════════
// Une ligne du blob rejoint la projection d'une chambre 'v2' SSI :
//   1. la chambre a une DATE DE BASCULE (table BASCULES_CSV ci-dessous) ;
//   2. la ligne porte le MARQUEUR d'origine CSV (`origine_import`, posé par
//      l'import depuis cette version) — un ancien import iCal à 0 € ne le porte
//      jamais, il n'est donc jamais compté ;
//   3. son arrivée est À PARTIR de la date de bascule (avant : rien ne change) ;
//   4. elle a un code stable (code HM Airbnb ou référence Booking) ;
//   5. QUI FAIT FOI (D2, décision Dimitri du 03/10) : si le même code existe
//      aussi dans la table `reservations` (toute chambre 'v2', tout statut,
//      annulée comprise), c'est la LIGNE CSV qui compte et la ligne V2 est
//      retirée de la projection (`codesReprisParLeCsv`) — compté une fois.
//      Tant qu'il n'y a pas de ligne CSV, la V2 compte. Un séjour arrivé
//      avant la bascule (à cheval) n'est pas repris : la V2 compte ;
//   6. ce n'est pas une réservation manuelle (déjà comptée par la fusion des
//      manuelles).
// MOIS FACTURÉ (D1, décision Dimitri du 03/10) : la projection ne RETIRE
// JAMAIS une ligne CSV déjà comptée — émettre la facture voyageur (série V)
// ne change pas le chiffre du mois. C'est l'IMPORT qui est refusé sur un mois
// facturé (`moisFactureVoyageur`, utilisé par csv-airbnb / csv-booking).
// Un même code n'est jamais compté deux fois, même présent dans deux mois ou
// deux chambres du blob. Une ligne SUPPRIMÉE (marque de deleteResaV2) reste
// rendue pour l'affichage grisé, mais ne bloque pas le même code ailleurs.
//
// LECTURE RATÉE (F13) : si la table `reservations` n'a pas pu être lue, on ne
// sait pas quels séjours elle contient → AUCUNE ligne CSV n'est ajoutée (sinon
// on compterait en double dès que la lecture revient). Rien n'est inventé.
//
// RÉVERSIBLE : retirer la date d'une chambre de BASCULES_CSV remet exactement
// le comportement d'avant. Aucune écriture en base.

const {
  findBlockingVoyageurInvoiceForMonth,
  findBlockingCommission,
} = require("./invoice-guard");
const { computeInvoiceMonthByNights } = require("./resa-invoice-month");

// Date de bascule par chambre ('AAAA-MM-JJ'). Une arrivée À PARTIR de cette
// date compte depuis le relevé CSV.
const BASCULES_CSV = Object.freeze({
  lc1: "2026-10-02",
  lc2: "2026-10-02",
  lc3: "2026-10-02",
});

// Maison Zen = UNE propriété Booking, une annonce par chambre : un relevé
// Booking mélange les trois chambres. L'import doit rattacher chaque ligne à
// sa chambre (lue dans le relevé, sinon proposée puis validée par Dimitri).
const GROUPES_BOOKING = Object.freeze([Object.freeze(["lc1", "lc2", "lc3"])]);

// Marqueur d'origine posé par l'import CSV sur chaque création / mise à jour.
const ORIGINE_CSV_AIRBNB = "csv_airbnb";
const ORIGINE_CSV_BOOKING = "csv_booking";

function dateBasculeCsv(lid, bascules) {
  const table = bascules || BASCULES_CSV;
  if (!lid || !table || typeof table !== "object") return null;
  if (!Object.prototype.hasOwnProperty.call(table, lid)) return null;
  const d = table[lid];
  return typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

function chambresDuGroupeBooking(lid) {
  if (!lid) return null;
  for (const groupe of GROUPES_BOOKING) {
    if (groupe.includes(lid)) return groupe.slice();
  }
  return null;
}

function estLigneCsv(r) {
  return (
    !!r &&
    (r.origine_import === ORIGINE_CSV_AIRBNB ||
      r.origine_import === ORIGINE_CSV_BOOKING)
  );
}

function codeV2DeLaLigne(row) {
  const raw = (row && row.raw_payload) || {};
  const ref = raw.apiReference != null ? String(raw.apiReference).trim() : "";
  return ref || null;
}

// Codes supprimés d'une chambre (marques posées par deleteResaV2 :
// data._tombstones[lid].airbnb_codes / booking_refs).
function codesSupprimes(data, lid) {
  const t = data && data._tombstones && data._tombstones[lid];
  const out = new Set();
  if (!t) return out;
  for (const x of Array.isArray(t.airbnb_codes) ? t.airbnb_codes : [])
    if (x && x.airbnbCode) out.add(String(x.airbnbCode));
  for (const x of Array.isArray(t.booking_refs) ? t.booking_refs : [])
    if (x && x.bookingRef) out.add(String(x.bookingRef));
  return out;
}

// `resasByLogement` est la Map rendue par la lecture de `reservations`. En cas
// d'échec, les deux lecteurs (écran et serveur) rendent une Map VIDE : vide
// alors qu'on attend des chambres 'v2' = lecture ratée ou pas encore arrivée.
function lectureV2Ratee(resasByLogement) {
  if (!resasByLogement || typeof resasByLogement.get !== "function")
    return true;
  return typeof resasByLogement.size === "number" && resasByLogement.size === 0;
}

// → { [lid]: { [mk]: resa[] } } — les lignes CSV à AJOUTER à la projection.
// Mêmes références que le blob (aucune copie, aucune mutation).
function collectCsvApresBascule(data, opts) {
  const o = opts || {};
  const v2Lids = o.v2Lids;
  if (!data || typeof data !== "object") return {};
  if (!Array.isArray(v2Lids) || v2Lids.length === 0) return {};
  const lidsAvecBascule = v2Lids.filter((lid) =>
    dateBasculeCsv(lid, o.bascules),
  );
  if (lidsAvecBascule.length === 0) return {};
  if (lectureV2Ratee(o.resasByLogement)) return {};

  const mks = Object.keys(data)
    .filter((mk) => /^\d{4}-\d{2}$/.test(mk))
    .sort();
  const dejaComptes = new Set();
  // Une ligne SUPPRIMÉE (marque de deleteResaV2) est rendue pour rester
  // visible grisée (« Restaurer » ; la projection la retire), mais elle ne
  // prend PAS la place du code : la même référence réimportée dans une autre
  // chambre y est comptée, une seule fois au total.
  const dejaGrises = new Set();
  const out = {};
  for (const lid of lidsAvecBascule) {
    const bascule = dateBasculeCsv(lid, o.bascules);
    const supprimes = codesSupprimes(data, lid);
    for (const mk of mks) {
      const bloc = data[mk] && data[mk][lid];
      const resas = bloc && bloc.reservations;
      if (!Array.isArray(resas) || resas.length === 0) continue;
      for (const r of resas) {
        if (!estLigneCsv(r)) continue;
        if (String(r.source) === "manual") continue;
        const code = r.airbnbCode || r.bookingRef || null;
        if (!code) continue;
        const arrivee = r.checkIn || r.date;
        if (typeof arrivee !== "string" || arrivee.slice(0, 10) < bascule)
          continue;
        const supprime = supprimes.has(String(code));
        if (supprime && dejaGrises.has(lid + "|" + code)) continue;
        if (!supprime && dejaComptes.has(String(code))) continue;
        if (supprime) dejaGrises.add(lid + "|" + code);
        else dejaComptes.add(String(code));
        if (!out[lid]) out[lid] = {};
        if (!out[lid][mk]) out[lid][mk] = [];
        out[lid][mk].push(r);
      }
    }
  }
  return out;
}

// D2 — codes REPRIS par une ligne CSV comptée (non supprimée) : leurs lignes
// V2 sortent de la projection, toutes chambres confondues (jamais de double
// compte). `csvParLid` = le retour de collectCsvApresBascule.
function codesReprisParLeCsv(csvParLid, data) {
  const out = new Set();
  for (const lid of Object.keys(csvParLid || {})) {
    const supprimes = codesSupprimes(data, lid);
    for (const mk of Object.keys(csvParLid[lid] || {})) {
      for (const r of csvParLid[lid][mk] || []) {
        const code = r && (r.airbnbCode || r.bookingRef);
        if (code && !supprimes.has(String(code))) out.add(String(code));
      }
    }
  }
  return out;
}

// Relecture tour 4 (point 3) — MOIS FACTURÉS HORS DE L'APPLICATION, pour les
// logements en MANDAT (pas les chambres de la sous-location, série V dans
// l'app). Août 2026 : factures propriétaires P-2026-0048 à P-2026-0055 faites
// à la main pendant la panne d'août-septembre 2026 (lib/factures-hors-app.js
// les décrit, mais elles ne sont pas en base et ce module ne porte pas la
// liste des mois). Constante datée du 06/10/2026 : à retirer le jour où ces
// factures seront enregistrées dans la table `invoices`.
// SEPTEMBRE 2026 N'Y EST PAS, volontairement : ses factures P-2026-0056 à
// P-2026-0063 sont dans l'application, donc septembre est déjà gelé par le
// gel P (findBlockingCommission). L'ajouter ici doublerait la règle.
const MOIS_FACTURES_HORS_APP = Object.freeze({
  "2026-08": "P-2026-0048 à P-2026-0055",
});

// D1 — (lid, mk) → true si le mois est FACTURÉ sur ce logement : facture
// voyageur (série V, sous-location) OU facture propriétaire (série P,
// mandats — relecture tour 3, même règle que la saisie à la main,
// findBlockingCommission). L'import CSV y est refusé.
//
// Rend `false` (mois libre) ou un objet VRAI qui nomme le gel :
//   { motif: "mois_facture", facture }          facture de l'app (V ou P) ;
//   { motif: "mois_facture_hors_app", numeros } mois facturé HORS de l'app.
function moisFactureVoyageur(invoicesRaw, monthKeyToLabel) {
  return (lid, mk) => {
    if (!lid || !mk) return false;
    // Relecture tour 4 (point 3) : mois facturés hors application (mandats).
    const horsApp = MOIS_FACTURES_HORS_APP[mk];
    if (horsApp && !chambresDuGroupeBooking(lid))
      return { motif: "mois_facture_hors_app", numeros: horsApp };
    if (typeof monthKeyToLabel !== "function") return false;
    const label = monthKeyToLabel(mk);
    if (!label || !Array.isArray(invoicesRaw)) return false;
    const q = { logId: lid, monthLabel: label };
    const facture =
      findBlockingVoyageurInvoiceForMonth(invoicesRaw, q) ||
      findBlockingCommission(invoicesRaw, q);
    return facture ? { motif: "mois_facture", facture } : false;
  };
}

// Relecture tour 3 (point 4) — mois EFFECTIF d'un code : déplacement de mois
// (data._monthOverrides[lid][code]) s'il existe, sinon le mois de rangement.
function moisEffectifDuCode(data, lid, code, mk) {
  const o =
    data && data._monthOverrides && data._monthOverrides[lid]
      ? data._monthOverrides[lid][String(code)]
      : null;
  return typeof o === "string" && /^\d{4}-\d{2}$/.test(o) ? o : mk;
}

// D (relecture argent R1) — (code) → { lid, mk } si la ligne V2 de ce code
// est rangée dans un mois FACTURÉ (moisGele), sinon null. Le relevé peut
// ranger le même séjour dans un mois libre (date décalée) : D2 retirerait
// alors la V2 d'un mois facturé. L'import est refusé dans ce cas aussi.
function moisFactureDuCodeV2(resasByLogement, moisGele, data) {
  const index = new Map();
  if (
    resasByLogement &&
    typeof resasByLogement.forEach === "function" &&
    typeof moisGele === "function"
  ) {
    resasByLogement.forEach((rows, lid) => {
      for (const row of Array.isArray(rows) ? rows : []) {
        const code = codeV2DeLaLigne(row);
        if (!code || index.has(code)) continue;
        const ci = typeof row.start_date === "string" ? row.start_date : null;
        const mk =
          computeInvoiceMonthByNights(row.start_date, row.end_date) ||
          (ci ? ci.slice(0, 7) : null);
        const l = row.logement_id || lid;
        // Mois effectif (déplacement compris, relecture tour 3 point 4).
        const mkEff = mk ? moisEffectifDuCode(data, l, code, mk) : null;
        if (mkEff && moisGele(l, mkEff)) index.set(code, { lid: l, mk: mkEff });
      }
    });
  }
  return (code) => (code != null && index.get(String(code))) || null;
}

module.exports = {
  MOIS_FACTURES_HORS_APP,
  moisFactureVoyageur,
  moisEffectifDuCode,
  moisFactureDuCodeV2,
  codesReprisParLeCsv,
  BASCULES_CSV,
  GROUPES_BOOKING,
  ORIGINE_CSV_AIRBNB,
  ORIGINE_CSV_BOOKING,
  dateBasculeCsv,
  chambresDuGroupeBooking,
  estLigneCsv,
  codeV2DeLaLigne,
  lectureV2Ratee,
  collectCsvApresBascule,
};
